import React from "react";
import {
  Box,
  Typography,
  Container,
  Paper,
  AppBar,
  Toolbar,
  List,
  ListItem,
  ListItemText,
  Grid,
  Button,
} from "@mui/material";
import {
  DirectionsBus,
  Description,
  Gavel,
  AccountCircle,
  Payment,
  Security,
  Assignment,
  Block,
  Warning,
  Update,
  ArrowBack,
  ContactMail,
} from "@mui/icons-material";
import { styled } from "@mui/material/styles";
import WhiteLogo from "../../assets/logos/gaadikharcha-logo-white.png";
import { useNavigate } from "react-router-dom";

const Section = styled(Box)(({ theme }) => ({
  padding: theme.spacing(6, 2),
  backgroundColor: "#fff",
}));

const TermsSection = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  marginBottom: theme.spacing(3),
  borderRadius: theme.spacing(2),
  border: "1px solid #e2e8f0",
  backgroundColor: "#ffffff",
  "&:hover": {
    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.05)",
  },
}));

const TermsOfConditions: React.FC = () => {
  const navigate = useNavigate();
  return (
    <Box sx={{ minHeight: "100vh", backgroundColor: "#f8fafc" }}>
      {/* Header */}
      <AppBar
        position="static"
        elevation={0}
        sx={{
          backgroundColor: "#1a365d",
          borderBottom: "1px solid #2d4a69",
          borderRadius: 0,
          zIndex: 1200,
        }}
      >
        <Toolbar
          sx={{
            justifyContent: "space-between",
            minHeight: "72px",
          }}
        >
          {/* Left side - GaadiKharcha.com with menu icon */}
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <img
              src={WhiteLogo}
              alt="GaadiKharcha"
              style={{ width: "120px", height: "auto" }}
            />
          </Box>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Button
              variant="text"
              color="inherit"
              onClick={() => navigate("/")}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                textTransform: "none",
              }}
            >
              <ArrowBack />
              Back to Home
            </Button>
          </Box>
        </Toolbar>
      </AppBar>
      {/* Hero Section */}
      <Box
        sx={{
          background: "linear-gradient(135deg, #1a365d 0%, #2563eb 100%)",
          color: "#ffffff",
          py: { xs: 4, md: 6 },
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Background Pattern */}
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: `
          linear-gradient(rgba(255,255,255,0.05) 1px, transparent 1px),
          linear-gradient(90deg, rgba(255,255,255,0.05) 1px, transparent 1px)
        `,
            backgroundSize: "20px 20px",
            opacity: 0.3,
          }}
        />

        <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
          <Box sx={{ textAlign: "center" }}>
            <Box sx={{ display: "flex", justifyContent: "center", mb: 1 }}>
              <Description
                sx={{
                  fontSize: 48,
                  color: "#60a5fa",
                  filter: "drop-shadow(0 4px 8px rgba(0,0,0,0.2))",
                }}
              />
            </Box>
            <Typography
              variant="h2"
              fontWeight={700}
              gutterBottom
              sx={{
                fontSize: { xs: "1.75rem", md: "2rem" },
                mb: 0.5,
                textShadow: "0 2px 4px rgba(0,0,0,0.1)",
              }}
            >
              Terms of Conditions
            </Typography>
            <Typography
              variant="h6"
              sx={{
                maxWidth: 500,
                mx: "auto",
                mb: 0.5,
                opacity: 0.9,
                lineHeight: 1.3,
                fontSize: "1rem",
              }}
            >
              Please read these terms carefully before using our service
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* Main Content */}
      <Section>
        <Container maxWidth="lg">
          <Grid container spacing={4}>
            <Grid item xs={12}>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                {/* Introduction */}
                <TermsSection elevation={0}>
                  <Typography
                    variant="body1"
                    color="#64748b"
                    sx={{ lineHeight: 1.8 }}
                  >
                    Welcome to Gaadikarcha.com, an Aequitous platform. Please
                    read these Terms and Conditions ("Terms", "Terms of
                    Service") carefully before using the Gaadikarcha.com web
                    application (the "Service") operated by Aequitous Pvt. Ltd.
                    (formerly Yuvajunction Services Pvt. Ltd.) ("us", "we", or
                    "our").
                  </Typography>
                  <Typography
                    variant="body1"
                    color="#64748b"
                    sx={{ lineHeight: 1.8, mt: 2 }}
                  >
                    By accessing or using the Service, you agree to be bound by
                    these Terms. If you do not agree with any part of the terms,
                    you may not use the Service.
                  </Typography>
                </TermsSection>

                {/* Service Overview */}
                <TermsSection elevation={0}>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                    <DirectionsBus
                      sx={{ color: "#60a5fa", mr: 2, fontSize: 24 }}
                    />
                    <Typography variant="h5" fontWeight={700} color="#1a365d">
                      1. Service Overview
                    </Typography>
                  </Box>
                  <Typography
                    variant="body1"
                    color="#64748b"
                    sx={{ lineHeight: 1.8 }}
                  >
                    Gaadikarcha.com is a digital platform designed to help fleet
                    owners manage their vehicle income, expenses, documents,
                    loans, repairs, and overall business performance. It is
                    offered as a subscription-based web application.
                  </Typography>
                </TermsSection>

                {/* User Accounts */}
                <TermsSection elevation={0}>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                    <AccountCircle
                      sx={{ color: "#10b981", mr: 2, fontSize: 24 }}
                    />
                    <Typography variant="h5" fontWeight={700} color="#1a365d">
                      2. User Accounts
                    </Typography>
                  </Box>
                  <List>
                    {[
                      "You must register an account to access most features of Gaadikarcha.com.",
                      "You are responsible for maintaining the confidentiality of your account login credentials.",
                      "You agree to provide accurate and complete information during registration and to keep it updated.",
                    ].map((item, index) => (
                      <ListItem key={index} sx={{ py: 0.5, px: 0 }}>
                        <ListItemText
                          primary={`• ${item}`}
                          sx={{
                            "& .MuiListItemText-primary": {
                              color: "#64748b",
                              lineHeight: 1.6,
                            },
                          }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </TermsSection>

                {/* Subscription & Payments */}
                <TermsSection elevation={0}>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                    <Payment sx={{ color: "#f59e0b", mr: 2, fontSize: 24 }} />
                    <Typography variant="h5" fontWeight={700} color="#1a365d">
                      3. Subscription & Payments
                    </Typography>
                  </Box>
                  <List>
                    {[
                      "Gaadikarcha.com is a paid service. Pricing plans and offers will be shared with you separately as per your business requirements.",
                      "Payments are to be made in advance and are non-refundable once the billing cycle begins.",
                      "Access to the platform may be suspended in case of non-payment.",
                    ].map((item, index) => (
                      <ListItem key={index} sx={{ py: 0.5, px: 0 }}>
                        <ListItemText
                          primary={`• ${item}`}
                          sx={{
                            "& .MuiListItemText-primary": {
                              color: "#64748b",
                              lineHeight: 1.6,
                            },
                          }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </TermsSection>

                {/* Data Usage & Security */}
                <TermsSection elevation={0}>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                    <Security sx={{ color: "#8b5cf6", mr: 2, fontSize: 24 }} />
                    <Typography variant="h5" fontWeight={700} color="#1a365d">
                      4. Data Usage & Security
                    </Typography>
                  </Box>
                  <List>
                    {[
                      "We ensure your data is stored securely and is used only to provide and improve our services.",
                      "You retain full ownership of your data. We do not sell or share your business data with any third party without your consent.",
                      "You are responsible for uploading only accurate and lawful content and documents.",
                    ].map((item, index) => (
                      <ListItem key={index} sx={{ py: 0.5, px: 0 }}>
                        <ListItemText
                          primary={`• ${item}`}
                          sx={{
                            "& .MuiListItemText-primary": {
                              color: "#64748b",
                              lineHeight: 1.6,
                            },
                          }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </TermsSection>

                {/* User Responsibilities */}
                <TermsSection elevation={0}>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                    <Assignment
                      sx={{ color: "#ef4444", mr: 2, fontSize: 24 }}
                    />
                    <Typography variant="h5" fontWeight={700} color="#1a365d">
                      5. User Responsibilities
                    </Typography>
                  </Box>
                  <Typography
                    variant="body1"
                    color="#64748b"
                    sx={{ lineHeight: 1.8, mb: 2 }}
                  >
                    By using Gaadikarcha.com, you agree:
                  </Typography>
                  <List>
                    {[
                      "Not to misuse the platform or use it for unlawful activities.",
                      "Not to duplicate, reverse engineer, or commercially exploit the platform.",
                      "To comply with all applicable laws and regulations.",
                    ].map((item, index) => (
                      <ListItem key={index} sx={{ py: 0.5, px: 0 }}>
                        <ListItemText
                          primary={`• ${item}`}
                          sx={{
                            "& .MuiListItemText-primary": {
                              color: "#64748b",
                              lineHeight: 1.6,
                            },
                          }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </TermsSection>

                {/* Termination */}
                <TermsSection elevation={0}>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                    <Block sx={{ color: "#60a5fa", mr: 2, fontSize: 24 }} />
                    <Typography variant="h5" fontWeight={700} color="#1a365d">
                      6. Termination
                    </Typography>
                  </Box>
                  <Typography
                    variant="body1"
                    color="#64748b"
                    sx={{ lineHeight: 1.8 }}
                  >
                    We reserve the right to suspend or terminate your access to
                    the platform at any time if you breach these Terms or misuse
                    the Service.
                  </Typography>
                </TermsSection>

                {/* Limitation of Liability */}
                <TermsSection elevation={0}>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                    <Warning sx={{ color: "#f59e0b", mr: 2, fontSize: 24 }} />
                    <Typography variant="h5" fontWeight={700} color="#1a365d">
                      7. Limitation of Liability
                    </Typography>
                  </Box>
                  <Typography
                    variant="body1"
                    color="#64748b"
                    sx={{ lineHeight: 1.8, mb: 2 }}
                  >
                    Gaadikarcha.com is provided on an "as is" basis. We are not
                    liable for any losses or damages arising out of:
                  </Typography>
                  <List>
                    {[
                      "Incorrect data entered by you",
                      "Network issues affecting alerts or reminders",
                      "Delay or disruption in service availability",
                    ].map((item, index) => (
                      <ListItem key={index} sx={{ py: 0.5, px: 0 }}>
                        <ListItemText
                          primary={`• ${item}`}
                          sx={{
                            "& .MuiListItemText-primary": {
                              color: "#64748b",
                              lineHeight: 1.6,
                            },
                          }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </TermsSection>

                {/* Changes to Terms */}
                <TermsSection elevation={0}>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                    <Update sx={{ color: "#10b981", mr: 2, fontSize: 24 }} />
                    <Typography variant="h5" fontWeight={700} color="#1a365d">
                      8. Changes to Terms
                    </Typography>
                  </Box>
                  <Typography
                    variant="body1"
                    color="#64748b"
                    sx={{ lineHeight: 1.8 }}
                  >
                    We may update these Terms from time to time. Any changes
                    will be notified through email or the platform. Continued
                    use of the Service after changes means you accept the
                    updated Terms.
                  </Typography>
                </TermsSection>

                {/* Governing Law */}
                <TermsSection elevation={0}>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                    <Gavel sx={{ color: "#8b5cf6", mr: 2, fontSize: 24 }} />
                    <Typography variant="h5" fontWeight={700} color="#1a365d">
                      9. Governing Law
                    </Typography>
                  </Box>
                  <Typography
                    variant="body1"
                    color="#64748b"
                    sx={{ lineHeight: 1.8 }}
                  >
                    These Terms are governed by the laws of India. Any disputes
                    will fall under the exclusive jurisdiction of the courts in
                    Mumbai, Maharashtra.
                  </Typography>
                </TermsSection>

                {/* Contact Us */}
                <TermsSection elevation={0}>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                    <ContactMail
                      sx={{ color: "#60a5fa", mr: 2, fontSize: 24 }}
                    />
                    <Typography variant="h5" fontWeight={700} color="#1a365d">
                      10. Contact Information
                    </Typography>
                  </Box>

                  <Typography
                    variant="body1"
                    color="#64748b"
                    sx={{ lineHeight: 1.8, mb: 3 }}
                  >
                    If you have any questions about this Privacy Policy or our
                    data practices, please contact us:
                  </Typography>

                  <Box
                    sx={{
                      p: 3,
                      borderRadius: 2,
                      backgroundColor: "#f8fafc",
                      border: "1px solid #e2e8f0",
                    }}
                  >
                    <Typography
                      variant="h6"
                      fontWeight={600}
                      color="#1a365d"
                      gutterBottom
                    >
                      Aequitous Services Pvt. Ltd. (formerly YSPL)
                    </Typography>
                    <Typography variant="body2" color="#64748b" sx={{ mb: 1 }}>
                      <strong>Email:</strong> info@aequitous.com
                    </Typography>
                    <Typography variant="body2" color="#64748b" sx={{ mb: 1 }}>
                      <strong>Phone:</strong> +91 22 4515 2489
                    </Typography>
                    <Typography variant="body2" color="#64748b">
                      <strong>Address:</strong> Mumbai, Maharashtra, India
                    </Typography>
                  </Box>
                </TermsSection>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Section>

      {/* Footer */}
      <Box
        component="footer"
        sx={{
          backgroundColor: "#0f172a",
          color: "#e2e8f0",
          py: 4,
          textAlign: "center",
        }}
      >
        <Container maxWidth="lg">
          <Typography variant="body2" sx={{ color: "#94a3b8" }}>
            © {new Date().getFullYear()} Aequitous Pvt. Ltd. All rights
            reserved.
          </Typography>
        </Container>
      </Box>
    </Box>
  );
};

export default TermsOfConditions;
